import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Loader2, KeyRound, ShieldCheck, PlayCircle, Clock, X, CheckCircle2, AlertTriangle, Eye, RefreshCw, Bot } from 'lucide-react';
import { elodge } from '@/lib/api';

const STATUS_TONE = {
  queued: 'bg-slate-100 text-slate-600 border-slate-200',
  running: 'bg-sky-100 text-sky-700 border-sky-200',
  awaiting_human: 'bg-amber-100 text-amber-700 border-amber-200',
  succeeded: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_ICON = {
  queued: Clock,
  running: Loader2,
  awaiting_human: AlertTriangle,
  succeeded: CheckCircle2,
  failed: X,
  cancelled: X,
};

export function ELodgePanel({ matter }) {
  const [creds, setCreds] = React.useState(null);
  const [jobs, setJobs] = React.useState([]);
  const [dryRun, setDryRun] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [credsOpen, setCredsOpen] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [supportedSubs, setSupportedSubs] = React.useState([]);

  const loadJobs = React.useCallback(() => {
    if (!matter?.ref) return;
    elodge.jobs(matter.ref).then(setJobs).catch(() => {});
  }, [matter]);

  React.useEffect(() => {
    elodge.credsStatus().then(setCreds).catch(() => {});
    elodge.supported().then((r) => setSupportedSubs(r.subclasses || [])).catch(() => {});
  }, []);

  React.useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll every 3s if any job is in-flight
  React.useEffect(() => {
    const inflight = jobs.some((j) => ['queued', 'running'].includes(j.status));
    if (!inflight) return;
    const iv = setInterval(loadJobs, 3000);
    return () => clearInterval(iv);
  }, [jobs, loadJobs]);

  const enqueue = async () => {
    if (!matter) return;
    setBusy(true); setErr('');
    try {
      const payload = {
        applicant: { name: matter.applicant, country: matter.country, family_name: (matter.applicant || '').split(' ').slice(-1)[0], given_names: (matter.applicant || '').split(' ').slice(0, -1).join(' ') },
        nominator: { name: 'ImmiPro Demo Nominator', abn: '51 824 753 556', anzsco: matter.occupation },
        skills: { assessing_body: matter.assessing_body, reference: matter.assessment_ref || 'ACS-STUB' },
      };
      await elodge.enqueue(matter.ref, matter.subclass, payload, dryRun);
      loadJobs();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to enqueue');
    } finally { setBusy(false); }
  };

  const openDetail = async (id) => {
    setDetail({ loading: true, id });
    try { const j = await elodge.job(id); setDetail(j); }
    catch (e) { setDetail({ error: e?.response?.data?.detail || 'Load failed', id }); }
  };

  const cancel = async (id) => { try { await elodge.cancel(id); loadJobs(); } catch { /* noop */ } };

  const subclassSupported = matter && supportedSubs.includes(String(matter.subclass));

  return (
    <Card className="border-[#1A3A5C]/20 bg-gradient-to-br from-[#1A3A5C] to-[#22507A] text-white" data-testid="elodge-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ECC9A]/20"><Bot className="h-4 w-4 text-[#2ECC9A]" /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#2ECC9A]">eLodge — Cloud Automation</div>
              <div className="mt-0.5 text-sm text-white/80">Playwright worker fills ImmiAccount on your behalf.</div>
            </div>
          </div>
          <Badge variant="outline" className="border-white/20 bg-white/5 text-[10px] text-white">
            Worker · AU-hosted
          </Badge>
        </div>

        {/* Credentials */}
        <div className="mt-4 rounded-md border border-white/10 bg-white/5 p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5" />
              <span>ImmiAccount credentials</span>
            </div>
            {creds?.configured ? (
              <Badge variant="outline" className="border-[#2ECC9A]/40 bg-[#2ECC9A]/10 text-[10px] text-[#2ECC9A]">
                <ShieldCheck className="mr-1 h-3 w-3" /> Vault-encrypted
              </Badge>
            ) : (
              <Badge variant="outline" className="border-amber-400/40 bg-amber-400/10 text-[10px] text-amber-300">Not set</Badge>
            )}
          </div>
          {creds?.configured && <div className="mt-1 font-mono text-[10px] text-white/60">User: {creds.username}</div>}
          <Button
            data-testid="elodge-configure-creds"
            onClick={() => setCredsOpen(true)}
            variant="outline"
            className="mt-2 h-7 w-full border-white/20 bg-white/5 text-[11px] text-white hover:bg-[#2ECC9A]/20"
          >
            {creds?.configured ? 'Rotate credentials' : 'Configure credentials'}
          </Button>
        </div>

        {/* Submit */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span>Dry-run (safe test)</span>
            <Switch data-testid="elodge-dryrun-toggle" checked={dryRun} onCheckedChange={setDryRun} />
          </div>
          {!subclassSupported && matter && (
            <div className="rounded-md bg-amber-500/10 p-2 text-[11px] text-amber-200">
              Subclass {matter.subclass} adapter not yet built. Supported today: {supportedSubs.join(', ') || 'none'}.
            </div>
          )}
          {!dryRun && !creds?.configured && (
            <div className="rounded-md bg-amber-500/10 p-2 text-[11px] text-amber-200">
              Configure DHA credentials before switching off dry-run.
            </div>
          )}
          <Button
            data-testid="elodge-submit"
            onClick={enqueue}
            disabled={busy || !subclassSupported || (!dryRun && !creds?.configured)}
            className="mt-1 h-9 w-full gap-1.5 bg-[#2ECC9A] text-[#1A3A5C] hover:bg-[#2ECC9A]/90"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            {dryRun ? 'Enqueue dry-run' : 'Submit to DHA'}
          </Button>
          {err && <div className="rounded-md bg-red-500/10 p-2 text-[11px] text-red-200" data-testid="elodge-error">{err}</div>}
        </div>

        {/* Job history */}
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-widest text-[#2ECC9A]">
            <span>Recent jobs</span>
            <button onClick={loadJobs} className="rounded p-1 text-white/70 hover:bg-white/10"><RefreshCw className="h-3 w-3" /></button>
          </div>
          <div className="space-y-1.5" data-testid="elodge-jobs">
            {jobs.length === 0 && <div className="rounded-md bg-white/5 p-2 text-[11px] text-white/50">No jobs yet.</div>}
            {jobs.map((j) => {
              const Ico = STATUS_ICON[j.status] || Clock;
              const spinning = j.status === 'running';
              return (
                <button
                  key={j.id}
                  data-testid={`elodge-job-${j.id}`}
                  onClick={() => openDetail(j.id)}
                  className="flex w-full items-center justify-between gap-2 rounded-md bg-white/5 p-2 text-left text-[11px] hover:bg-white/10"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Ico className={`h-3.5 w-3.5 shrink-0 ${spinning ? 'animate-spin' : ''}`} />
                    <span className="truncate">Subclass {j.subclass}{j.dry_run ? ' · dry' : ''}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant="outline" className={`text-[9px] capitalize ${STATUS_TONE[j.status] || ''}`}>
                      {j.status.replace('_', ' ')}
                    </Badge>
                    <Eye className="h-3 w-3 text-white/50" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </CardContent>

      <CredsDialog
        open={credsOpen}
        onOpenChange={setCredsOpen}
        onSaved={() => elodge.credsStatus().then(setCreds)}
      />
      <JobDetailDialog job={detail} onClose={() => setDetail(null)} onCancel={cancel} />
    </Card>
  );
}

function CredsDialog({ open, onOpenChange, onSaved }) {
  const [u, setU] = React.useState('');
  const [p, setP] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await elodge.saveCreds(u, p);
      onSaved?.();
      onOpenChange(false);
      setU(''); setP('');
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>DHA ImmiAccount credentials</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-600">
            Stored Fernet-encrypted in the vault. Only the worker can decrypt at runtime.
          </div>
          <div><Label>Username / ImmiAccount ID</Label><Input value={u} onChange={(e) => setU(e.target.value)} className="mt-1" autoComplete="off" data-testid="creds-username" /></div>
          <div><Label>Password</Label><Input value={p} onChange={(e) => setP(e.target.value)} type="password" className="mt-1" autoComplete="off" data-testid="creds-password" /></div>
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="creds-save" onClick={save} disabled={busy || !u || !p} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JobDetailDialog({ job, onClose, onCancel }) {
  if (!job) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>eLodge job · {job.matter_ref || '…'}</DialogTitle></DialogHeader>
        {job.loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#2ECC9A]" /></div>
        ) : job.error ? (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{job.error}</div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Status" value={<Badge variant="outline" className={`capitalize ${STATUS_TONE[job.status] || ''}`}>{(job.status || '').replace('_', ' ')}</Badge>} />
              <Field label="Subclass" value={job.subclass} />
              <Field label="Attempts" value={`${job.attempts} / ${job.max_attempts}`} />
              <Field label="Mode" value={job.dry_run ? 'Dry-run (no submission)' : 'Live'} />
              <Field label="Created" value={new Date(job.created_at).toLocaleString('en-AU')} />
              <Field label="Updated" value={new Date(job.updated_at).toLocaleString('en-AU')} />
            </div>
            {job.result && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="elodge-job-result">
                <div className="font-semibold">DHA response</div>
                <pre className="mt-1 whitespace-pre-wrap font-mono">{JSON.stringify(job.result, null, 2)}</pre>
              </div>
            )}
            {job.error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                <div className="font-semibold">Error</div>
                <div className="mt-1">{job.error}</div>
              </div>
            )}
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Timeline</div>
              <div className="max-h-80 space-y-1 overflow-auto rounded-md border border-slate-200 p-2 text-xs">
                {(job.logs || []).map((l, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[9px] uppercase ${l.level === 'error' ? 'bg-red-100 text-red-700' : l.level === 'warn' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {l.level}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-slate-500 text-[10px]">{new Date(l.at).toLocaleTimeString('en-AU')}</div>
                      <div className="text-slate-800">{l.msg}</div>
                      {l.screenshot && (
                        <img
                          alt="worker screenshot"
                          src={`data:image/jpeg;base64,${l.screenshot}`}
                          className="mt-1 max-w-full rounded border border-slate-200"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {['queued', 'awaiting_human'].includes(job.status) && (
            <Button variant="outline" onClick={() => { onCancel(job.id); onClose(); }} className="border-red-200 text-red-600">Cancel job</Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-800">{value}</div>
    </div>
  );
}
